/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import QuickLinks from './quick-link'
import StandardLinks from './standard-link'
import CopyrightSection from './copyright-section'
import CopyrightSectionKenwood from './kenwood/copyright-section-kenwood'
import CopyrightSectionBraun from './braun/copyright-section-braun'
import {useGetFooterByContentType} from '../../custom-api/hooks/use-get-footer'
import PreFooter from '../pre-footer'
import {BRAND_KEY} from '../../brand'
import useUtils from '../../custom-hooks/use-utils-helper'
import {useLocation} from 'react-router-dom'
import QuickLinksBraun from './braun/quick-link'

const Footer = ({...otherProps}) => {
    const location = useLocation()
    const {isCheckout, isCart, isMaintenance} = useUtils().currentPage(location.pathname)
    const isFooterLite = isCart || isCheckout || isMaintenance
    const styles = useMultiStyleConfig('Footer', {variant: isFooterLite ? 'checkout' : 'default'})

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const {data, isFetching, isLoading, isError} = useGetFooterByContentType()

    const footerGroup = data?.footer_content?.footer_reference_components || []

    // Helper function to get the correct component data based on _content_type_uid
    const getComponentData = (contentType) => {
        return footerGroup.find((item) => item?._content_type_uid === contentType)
    }

    const footerBenefit = getComponentData('footerbenefit')
    const footerSubscribe = getComponentData('footersubscribe')
    const footerQuickLinks = getComponentData('footerquicklinks')
    const footerStandardLinks = getComponentData('footerstandardlinks')
    const footerBottom = getComponentData('footerbottom')

    const showPreFooterContent = footerBenefit || footerSubscribe
    const showFooterContent = footerQuickLinks || footerStandardLinks || footerBottom

    return (
        <Box className="del-footer" {...styles.footerOuterContainer}>
            {!isFooterLite && showPreFooterContent && (
                <PreFooter
                    data={{
                        ...footerBenefit,
                        ...footerSubscribe,
                    }}
                />
            )}
            {showFooterContent && (
                <Box
                    as="footer"
                    {...styles.container}
                    {...otherProps}
                    data-testid="footer-container"
                >
                    {!isFooterLite &&
                        footerQuickLinks &&
                        footerQuickLinks?.quick_links?.length > 0 &&
                        (BRAND_KEY === 'braun' ? (
                            <QuickLinksBraun quickLinksSchema={footerQuickLinks.quick_links} />
                        ) : (
                            <QuickLinks quickLinksSchema={footerQuickLinks.quick_links} />
                        ))}
                    {!isFooterLite &&
                        footerStandardLinks &&
                        footerStandardLinks?.link_group?.length > 0 && (
                            <StandardLinks linkGroupSchema={footerStandardLinks.link_group} />
                        )}
                    {footerBottom &&
                        (BRAND_KEY === 'kenwood' ? (
                            <CopyrightSectionKenwood data={footerBottom} />
                        ) : BRAND_KEY === 'braun' ? (
                            <CopyrightSectionBraun data={footerBottom} />
                        ) : (
                            <CopyrightSection data={footerBottom} />
                        ))}
                </Box>
            )}
        </Box>
    )
}

export default Footer
